import { useState, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const MAX_RETRIES = 5;
const MAX_BACKOFF_MS = 30000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

/**
 * Check if a JWT access token is expired (with 30s buffer for clock skew).
 */
function isTokenExpired(token) {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 <= Date.now() + 30000;
  } catch {
    return true;
  }
}

/**
 * Refresh the access token using the stored refresh token.
 * Uses fetch (not Axios) to avoid coupling with the Axios interceptor.
 * Returns the new access token string, or null on failure.
 */
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    localStorage.setItem('access_token', data.access);
    return data.access;
  } catch {
    return null;
  }
}

/**
 * Get a valid (non-expired) access token, refreshing if necessary.
 */
async function getValidToken() {
  let token = localStorage.getItem('access_token');
  if (!token) return null;

  if (isTokenExpired(token)) {
    token = await refreshAccessToken();
  }

  return token;
}

/**
 * Subscribe to real-time agent session updates via Server-Sent Events.
 *
 * Connects to the SSE endpoint when `sessionId` is truthy and
 * automatically disconnects on terminal states or unmount.
 * Reconnects with exponential backoff on errors, refreshing JWT if expired.
 *
 * @param {string|null} sessionId - UUID of the agent session, or null/undefined to disable.
 * @returns {{
 *   session: object|null,
 *   error: string|null,
 *   isConnected: boolean,
 *   isReconnecting: boolean,
 *   retryCount: number,
 * }}
 */
function useAgentSession(sessionId) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const eventSourceRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const sessionRef = useRef(null);
  const connectRef = useRef(null);

  const cleanup = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setIsReconnecting(false);
    retryCountRef.current = 0;
    setRetryCount(0);
  }, []);

  useEffect(() => {
    if (!sessionId) {
      cleanup();
      return;
    }

    const connect = async () => {
      const token = await getValidToken();
      if (!token) {
        setError('Not authenticated');
        setIsReconnecting(false);
        return;
      }

      const url = `${API_BASE}/agents/sessions/${sessionId}/stream/?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => {
        setIsConnected(true);
        setError(null);
        setIsReconnecting(false);
        retryCountRef.current = 0;
        setRetryCount(0);
      };

      es.addEventListener('session_update', (event) => {
        try {
          const data = JSON.parse(event.data);
          setSession(data);
          sessionRef.current = data;
        } catch {
          setError('Failed to parse session update');
        }
      });

      es.addEventListener('done', () => {
        cleanup();
      });

      es.addEventListener('timeout', () => {
        setError('Stream timeout \u2014 connection closed after 30 minutes');
        cleanup();
      });

      es.onerror = () => {
        // Close the broken EventSource
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);

        // Don't reconnect if session is in a terminal state
        const currentSession = sessionRef.current;
        if (currentSession && TERMINAL_STATUSES.has(currentSession.status)) {
          return;
        }

        // Don't reconnect if max retries exceeded
        if (retryCountRef.current >= MAX_RETRIES) {
          setError('Connection failed \u2014 please refresh the page');
          setIsReconnecting(false);
          return;
        }

        // Don't attempt if offline — wait for online event
        if (!navigator.onLine) {
          setIsReconnecting(true);
          setError('You are offline. Will reconnect when back online.');
          return;
        }

        // Schedule reconnection with exponential backoff
        const attempt = retryCountRef.current + 1;
        retryCountRef.current = attempt;
        setRetryCount(attempt);
        setIsReconnecting(true);
        setError(null);

        const delay = Math.min(1000 * Math.pow(2, attempt - 1), MAX_BACKOFF_MS);

        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          connect();
        }, delay);
      };
    };

    connectRef.current = connect;
    connect();

    return cleanup;
  }, [sessionId, cleanup]);

  // Monitor online/offline status for reconnection
  useEffect(() => {
    const handleOnline = () => {
      if (sessionId && retryCountRef.current < MAX_RETRIES && !eventSourceRef.current) {
        setError(null);
        connectRef.current?.();
      }
    };

    const handleOffline = () => {
      if (!eventSourceRef.current && sessionId) {
        setIsReconnecting(true);
        setError('You are offline. Will reconnect when back online.');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [sessionId]);

  return { session, error, isConnected, isReconnecting, retryCount };
}

export default useAgentSession;
